import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getDraftCheckOutItems } from 'services';
import { getExternalClientPermissions, clientIdsWithExternalPermission } from './helpers/externalClientPermissions';

interface CartContextType {
  cart: any[];
  setCart: (items: any[]) => void;
  refreshCartItems: (clientId?: number) => Promise<void>;
  cartCount: number;
  selectedClientId: number | null;
  selectedClientName: string;
  setSelectedClientId: (clientId: number, clientName?: string) => void;
  eskiesToFit: boolean;
  setEskiesToFit: (value: boolean) => void;
  isClientSelected: boolean;
  updateClientSelected: (value: boolean) => void;
  quoteAnalysisRefID: string | null;
  setQuoteAnalysisRefID: (value: string | null) => void;
  quoteGroups: any[];
  setQuoteGroups: (groups: any[]) => void;
}

export const extractQuoteGroups = (data: any) => {
  const draftBottleGroups = Array.isArray(data?.draftBottleGroups)
    ? data.draftBottleGroups
    : data?.draftBottleGroup
      ? [data.draftBottleGroup]
      : [];

  return draftBottleGroups
    .filter((group: any) => group?.quoteAnalysisRefID != null)
    .map((group: any) => ({
      id: group.id ?? group.draftGroupID ?? '',
      name: `${group.name ?? ''}`.trim(),
      size: group.size ?? '',
      quoteAnalysisRefID: group.quoteAnalysisRefID,
      quoteAnalysisRefComments: group.quoteAnalysisRefComments ?? ''
    }));
};

const normalizeDraftCheckoutResponse = (data: any) => {
  const draftItems = Array.isArray(data?.draftItems)
    ? data.draftItems
    : [];

  const draftBottleGroups = Array.isArray(data?.draftBottleGroups)
    ? data.draftBottleGroups
    : data?.draftBottleGroup &&
      Array.isArray(data.draftBottleGroup.draftItems)
      ? [data.draftBottleGroup]
      : [];

  const groupedDraftItems = draftBottleGroups.flatMap((group: any) => {
    const groupedItems = Array.isArray(group?.draftItems)
      ? group.draftItems
      : [];

    return groupedItems.map((item: any, index: number) => ({
      ...item,

      draftBottleGroupID: group.id ?? group.draftGroupID ?? null,
      draftBottleGroupName: group.name || '',
      draftBottleGroupSize: group.size ?? '',
      draftBottleGroupClientID:
        group.clientID ?? group.clientId ?? null,
      draftBottleGroupOrder: index,
      quoteAnalysisRefID: group.quoteAnalysisRefID ?? null,
      quoteAnalysisRefComments:
        group.quoteAnalysisRefComments ?? '',
    }));
  });

  // Add quote information to normal draft items too
  const normalizedDraftItems = draftItems.map((item: any) => ({
    ...item
  }));

  return [...normalizedDraftItems, ...groupedDraftItems];
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCartState] = useState<any[]>([]);
  const [quoteGroups, setQuoteGroups] = useState<any[]>([]);

  const setCart = useCallback((items: any[]) => {
    setCartState(items);
    if (!items?.length) {
      setQuoteGroups([]);
    }
  }, []);
  const [eskiesToFit, setEskiesToFit] = useState<boolean>(false);
  const [isClientSelected, setIsClientSelected] = useState(false);
  const [quoteAnalysisRefID, setQuoteAnalysisRefID] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(() => {
    const saved = localStorage.getItem('selectedClientId');
    return saved ? parseInt(saved) : null;
  });
  const [selectedClientName, setSelectedClientName] = useState<string>(() => localStorage.getItem('selectedClientName') || '');

  const updateClientSelected = useCallback((value: boolean) => {
    setIsClientSelected(value);
  }, []);

  useEffect(() => {
    if (selectedClientId !== null) {
      localStorage.setItem('selectedClientId', selectedClientId.toString());
    } else {
      localStorage.removeItem('selectedClientId');
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedClientName) {
      localStorage.setItem('selectedClientName', selectedClientName);
    } else {
      localStorage.removeItem('selectedClientName');
    }
  }, [selectedClientName]);

  const refreshCartItems = useCallback(async (clientId?: number) => {
    try {
      const targetClientId = clientId ?? selectedClientId;

      if (!targetClientId || targetClientId === -1) {
        setCart([]);
        return;
      }

      const jwt = localStorage.getItem('token');
      const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

      if (labUserCheck?.UserType === 'External') {
        const externalClientPermissions = getExternalClientPermissions(labUserCheck?.ClientPermissions);
        const bottleOrderClientIds = clientIdsWithExternalPermission(externalClientPermissions, 'BottleOrders');

        if (!bottleOrderClientIds.has(String(targetClientId))) {
          setCart([]);
          return;
        }
      }

      const response = await getDraftCheckOutItems(targetClientId);
      if (response.status === 200) {
        setCart(normalizeDraftCheckoutResponse(response?.data));
        setQuoteGroups(extractQuoteGroups(response?.data));
      } else {
        setCart([]);
      }
    } catch (error) {
      // console.error('Error refreshing cart items:', error);
      setCart([]);
    }
  }, [selectedClientId, setCart]);

  const updateSelectedClientId = useCallback((clientId: number, clientName = '') => {
    setSelectedClientId(clientId);
    if (clientId === -1 || clientId === 0 || clientId === null) {
      setSelectedClientName('');
    } else if (clientName) {
      setSelectedClientName(clientName);
    }
    refreshCartItems(clientId);
  }, [refreshCartItems]);

  const cartCount = cart.length;

  return (
    <CartContext.Provider value={{
      cart,
      setCart,
      refreshCartItems,
      cartCount,
      selectedClientId,
      selectedClientName,
      setSelectedClientId: updateSelectedClientId,
      eskiesToFit,
      setEskiesToFit,
      isClientSelected,
      updateClientSelected,
      quoteAnalysisRefID,
      setQuoteAnalysisRefID,
      quoteGroups,
      setQuoteGroups
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};